import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Trophy, MessageSquare, Plus, Heart, MessageCircle, Zap, Clock, Sparkles, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MissionCard({ mission, onComplete }) {
  const icons = { daily: Clock, weekly: Trophy, special: Sparkles };
  const colors = { daily: 'text-green-400', weekly: 'text-blue-400', special: 'text-[#D4AF37]' };
  const Icon = icons[mission.mission_type] || Clock;
  const color = colors[mission.mission_type] || 'text-white/60';
  return (
    <div className={`mission-card ${mission.mission_type}`} data-testid={`mission-card-${mission.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center"><Icon size={20} className={color} /></div>
        <div className="flex-1">
          <span className={`text-xs uppercase font-semibold ${color}`}>{mission.mission_type}</span>
          <h3 className="text-white font-semibold mb-1">{mission.title}</h3>
          <p className="text-sm text-white/60 mb-3">{mission.description}</p>
          <div className="flex gap-4 mb-4">
            <span className="text-xs text-[#D4AF37] flex items-center gap-1"><Zap size={14} /> +{mission.token_reward} AIH</span>
            <span className="text-xs text-blue-400">+{mission.xp_reward} XP</span>
          </div>
          <Button size="sm" className="btn-primary w-full" onClick={() => onComplete(mission.id)} data-testid={`claim-mission-${mission.id}`}>Claim</Button>
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, onLike, categories }) {
  const cat = categories.find(c => c.id === post.category);
  return (
    <div className="forum-post" data-testid={`forum-post-${post.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
          <span className="text-black font-bold text-sm">{post.author_username?.charAt(0).toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-white font-semibold">{post.author_username}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full bg-white/5 ${cat?.color || 'text-white/60'}`}>{cat?.label || post.category}</span>
          </div>
          <h3 className="text-lg text-white font-semibold mb-2">{post.title}</h3>
          <p className="text-white/70 mb-4">{post.content}</p>
          <div className="flex gap-6">
            <button onClick={() => onLike(post.id)} className="flex items-center gap-2 text-white/50 hover:text-red-400" data-testid={`like-post-${post.id}`}><Heart size={18} /><span>{post.likes}</span></button>
            <div className="flex items-center gap-2 text-white/50"><MessageCircle size={18} /><span>{post.replies_count} replies</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Community = () => {
  const { user, token, isAuthenticated, updateUser } = useAuth();
  const [missions, setMissions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'general' });
  const [posting, setPosting] = useState(false);
  const [open, setOpen] = useState(false);

  const categories = [
    { id: 'general', label: 'General', color: 'text-white/60' },
    { id: 'web3', label: 'Web3', color: 'text-blue-400' },
    { id: 'entrepreneurship', label: 'Business', color: 'text-green-400' },
    { id: 'ai', label: 'AI', color: 'text-purple-400' },
    { id: 'creator', label: 'Creator', color: 'text-orange-400' },
  ];

  useEffect(() => {
    Promise.all([axios.get(`${API}/missions`), axios.get(`${API}/forum/posts`)]).then(([m, p]) => { setMissions(m.data); setPosts(p.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const completeMission = async (id) => {
    if (!isAuthenticated) return toast.error('Please login');
    try {
      const r = await axios.post(`${API}/missions/${id}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`+${r.data.tokens_earned} AIH`);
      updateUser({ aih_tokens: (user.aih_tokens || 0) + r.data.tokens_earned, xp_points: (user.xp_points || 0) + r.data.xp_earned });
      setMissions(missions.filter(m => m.id !== id));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const createPost = async () => {
    if (!newPost.title || !newPost.content) return toast.error('Fill all fields');
    setPosting(true);
    try {
      const r = await axios.post(`${API}/forum/posts`, newPost, { headers: { Authorization: `Bearer ${token}` } });
      setPosts([r.data, ...posts]);
      setNewPost({ title: '', content: '', category: 'general' });
      setOpen(false);
      toast.success('Posted!');
    } catch (e) { toast.error('Failed'); }
    setPosting(false);
  };

  const likePost = async (id) => {
    if (!isAuthenticated) return toast.error('Please login');
    try {
      await axios.post(`${API}/forum/posts/${id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(posts.map(p => p.id === id ? { ...p, likes: p.likes + 1 } : p));
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="community-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-white mb-2 flex items-center gap-3"><Users className="text-[#D4AF37]" /> Community Hub</h1>
        </div>

        <Tabs defaultValue="missions">
          <TabsList className="bg-[#0A1628] mb-8">
            <TabsTrigger value="missions" className="flex items-center gap-2" data-testid="missions-tab"><Trophy size={16} /> Missions</TabsTrigger>
            <TabsTrigger value="forum" className="flex items-center gap-2" data-testid="forum-tab"><MessageSquare size={16} /> Forum</TabsTrigger>
          </TabsList>

          <TabsContent value="missions">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {missions.map(m => <MissionCard key={m.id} mission={m} onComplete={completeMission} />)}
            </div>
            {missions.length === 0 && <div className="text-center py-16"><Trophy size={48} className="mx-auto text-white/20 mb-4" /><p className="text-white/60">No missions</p></div>}
          </TabsContent>

          <TabsContent value="forum">
            {isAuthenticated && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button className="btn-primary mb-6" data-testid="create-post-btn"><Plus className="mr-2" size={18} /> Create Post</Button></DialogTrigger>
                <DialogContent className="bg-[#0A1628] border-white/10">
                  <DialogHeader><DialogTitle className="font-unbounded text-white">New Post</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div><Label className="text-white/80">Title</Label><Input placeholder="Title..." value={newPost.title} onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} className="input-cosmic mt-2" data-testid="post-title-input" /></div>
                    <div><Label className="text-white/80">Category</Label>
                      <Select value={newPost.category} onValueChange={(v) => setNewPost({ ...newPost, category: v })}>
                        <SelectTrigger className="input-cosmic mt-2" data-testid="post-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0A1628] border-white/10">{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-white/80">Content</Label><Textarea placeholder="Write..." value={newPost.content} onChange={(e) => setNewPost({ ...newPost, content: e.target.value })} className="input-cosmic mt-2 min-h-[120px]" data-testid="post-content-input" /></div>
                    <Button className="w-full btn-primary" onClick={createPost} disabled={posting} data-testid="submit-post-btn"><Send className="mr-2" size={18} /> {posting ? '...' : 'Post'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <div className="space-y-4">
              {posts.map(p => <PostCard key={p.id} post={p} onLike={likePost} categories={categories} />)}
            </div>
            {posts.length === 0 && <div className="text-center py-16"><MessageSquare size={48} className="mx-auto text-white/20 mb-4" /><p className="text-white/60">No posts yet</p></div>}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Community;
